import { formatPrice } from '../utils/Currency';

// WhatsApp Business API service for sending offer messages
export const sendOfferMessage = async (lead, offers, currency = 'ZAR', businessId = null) => {
    try {
        console.log('Sending WhatsApp offer message:', { lead, offers });
        
        // Get business WhatsApp configuration
        const whatsappConfig = await getBusinessWhatsAppConfig(businessId);
        
        if (!whatsappConfig) {
            console.log('WhatsApp not configured for this business, skipping message send');
            return [{ success: false, reason: 'WhatsApp not configured for this business' }];
        }

        // Check if WhatsApp is enabled for sending messages
        if (!whatsappConfig.enabled) {
            console.log('WhatsApp is disabled for this business, skipping message send');
            return [{ success: false, reason: 'WhatsApp is disabled for this business' }];
        }
        
        if (!lead.phone) {
            console.log('No phone number provided for lead');
            return [{ success: false, reason: 'No phone number provided' }];
        }
        
        // Format phone number (remove spaces, add country code if needed)
        let formattedPhone = lead.phone.replace(/\s+/g, '');
        if (!formattedPhone.startsWith('+')) {
            // Assume South African number if no country code
            if (formattedPhone.startsWith('0')) {
                formattedPhone = '+27' + formattedPhone.substring(1);
            } else {
                formattedPhone = '+27' + formattedPhone;
            }
        }
        
        // Create offer message
        const offerText = createOfferMessage(lead, offers, currency);
        
        // Send message via WhatsApp Business API
        const response = await fetch(`https://graph.facebook.com/v18.0/${whatsappConfig.phoneNumberId}/messages`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${whatsappConfig.accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                messaging_product: 'whatsapp',
                to: formattedPhone,
                type: 'text',
                text: {
                    body: offerText
                }
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            console.error('WhatsApp API error:', errorData);
            
            // Handle specific error cases
            if (response.status === 401) {
                return [{ 
                    success: false, 
                    reason: 'WhatsApp access token is invalid or expired', 
                    error: errorData 
                }];
            }
            
            return [{ 
                success: false, 
                reason: `WhatsApp API error (${response.status})`, 
                error: errorData 
            }];
        }
        
        const result = await response.json();
        console.log('WhatsApp message sent successfully:', result);
        
        return [{
            success: true,
            phoneNumber: formattedPhone,
            messageId: result.messages?.[0]?.id,
            offer: offers[0] // Return the first offer for reference
        }];
        
    } catch (error) {
        console.error('Error sending WhatsApp message:', error);
        return [{ 
            success: false, 
            reason: 'Network or other error', 
            error: error.message 
        }];
    }
};

// Helper function to create the offer message text
const createOfferMessage = (lead, offers, currency = 'ZAR') => {
    const businessName = 'TradePoint'; // This could be dynamic from business settings
    const leadName = lead.displayName || 'Valued Customer';
    
    let message = `Hello ${leadName}! 👋\n\n`;
    message += `Thank you for your interest! We have some great offers for you:\n\n`;
    
    offers.forEach((offer, index) => {
        message += `📱 *${offer.productName}*\n`;
        
        message += `💰 Price: ${formatPrice(offer.price, currency)}\n`;
        message += `📋 Condition: ${offer.condition}\n`;
        if (offer.variant && offer.variant !== 'Standard') {
            message += `🔧 Variant: ${offer.variant}\n`;
        }
        message += `📂 Category: ${offer.category}\n`;
        if (offer.imageUrl) {
            message += `🖼️ Product image available\n`;
        }
        message += `\n`;
    });
    
    message += `Interested? Reply to this message or call us for more details!\n\n`;
    message += `Best regards,\n${businessName} Team`;
    
    return message;
};

// Send individual WhatsApp message
export const sendWhatsAppMessage = async (messageData, businessId = null) => {
    try {
        // Log without sensitive data
        console.log('Sending WhatsApp message to:', messageData.to ? '***' + messageData.to.slice(-4) : 'unknown');
        
        // Get business WhatsApp configuration
        const whatsappConfig = await getBusinessWhatsAppConfig(businessId);
        
        if (!whatsappConfig) {
            console.log('WhatsApp not configured for this business, skipping message send');
            return { success: false, error: 'WhatsApp not configured for this business' };
        }

        // Check if WhatsApp is enabled for sending messages
        if (!whatsappConfig.enabled) {
            console.log('WhatsApp is disabled for this business, skipping message send');
            return { success: false, error: 'WhatsApp is disabled for this business' };
        }
        
        if (!messageData.to) {
            console.log('No phone number provided');
            return { success: false, error: 'No phone number provided' };
        }
        
        // Format phone number (remove spaces, add country code if needed)
        let formattedPhone = messageData.to.replace(/\s+/g, '');
        if (!formattedPhone.startsWith('+')) {
            // Assume South African number if no country code
            if (formattedPhone.startsWith('0')) {
                formattedPhone = '+27' + formattedPhone.substring(1);
            } else {
                formattedPhone = '+27' + formattedPhone;
            }
        }
        
        // Create message payload
        const messagePayload = {
            messaging_product: "whatsapp",
            to: formattedPhone,
            type: "text",
            text: {
                body: messageData.message
            }
        };
        
        // Send message via WhatsApp Business API
        const response = await fetch(
            `https://graph.facebook.com/v18.0/${whatsappConfig.phoneNumberId}/messages`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${whatsappConfig.accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(messagePayload)
            }
        );
        
        const result = await response.json();
        
        if (response.ok && result.messages && result.messages.length > 0) {
            console.log('WhatsApp message sent successfully:', result);
            return {
                success: true,
                messageId: result.messages[0].id,
                data: result
            };
        } else {
            console.error('WhatsApp API error:', result);
            
            // Handle specific error cases
            if (response.status === 401) {
                return {
                    success: false,
                    error: 'WhatsApp access token is invalid or expired. Please update your WhatsApp settings.',
                    data: result
                };
            }
            
            return {
                success: false,
                error: result.error?.message || `Failed to send message (${response.status})`,
                data: result
            };
        }
        
    } catch (error) {
        console.error('Error sending WhatsApp message:', error);
        return { success: false, error: error.message };
    }
};

// Get business WhatsApp configuration from Firestore
const getBusinessWhatsAppConfig = async (businessId) => {
    if (!businessId) {
        console.error('No business ID provided');
        return null;
    }

    try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        const businessRef = doc(db, "businesses", businessId);
        const businessDoc = await getDoc(businessRef);
        
        if (!businessDoc.exists()) {
            console.error('Business document not found');
            return null;
        }
        
        const businessData = businessDoc.data();
        const whatsappConfig = businessData.whatsappConfig;
        
        if (!whatsappConfig || !whatsappConfig.accessToken || !whatsappConfig.phoneNumberId) {
            console.log('WhatsApp not configured for this business');
            return null;
        }
        
        return {
            accessToken: whatsappConfig.accessToken,
            phoneNumberId: whatsappConfig.phoneNumberId,
            businessName: whatsappConfig.businessName || businessData.businessName || 'Business',
            enabled: whatsappConfig.enabled || false
        };
    } catch (error) {
        // Handle permission errors gracefully
        if (error.code === 'permission-denied' || error.message?.includes('permissions')) {
            console.warn('Insufficient permissions to access WhatsApp configuration');
            return null;
        }
        console.error('Error fetching business WhatsApp configuration:', error.message);
        return null;
    }
};

// Test WhatsApp configuration for a specific business
export const testWhatsAppConfig = async (businessId) => {
    try {
        console.log('Testing WhatsApp configuration for business:', businessId);
        
        const whatsappConfig = await getBusinessWhatsAppConfig(businessId);
        
        if (!whatsappConfig) {
            console.error('WhatsApp not configured for this business');
            return false;
        }
        
        console.log('WhatsApp config found:', {
            hasAccessToken: !!whatsappConfig.accessToken,
            hasPhoneNumberId: !!whatsappConfig.phoneNumberId,
            businessName: whatsappConfig.businessName
        });
        
        // Test by making a simple API call to verify credentials
        const response = await fetch(`https://graph.facebook.com/v18.0/${whatsappConfig.phoneNumberId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${whatsappConfig.accessToken}`,
            }
        });
        
        const result = await response.json();
        console.log('WhatsApp API test response:', {
            status: response.status,
            ok: response.ok,
            data: result
        });
        
        if (!response.ok) {
            console.error('WhatsApp API error:', result);
        }
        
        return response.ok;
    } catch (error) {
        console.error('WhatsApp configuration test failed:', error);
        return false;
    }
};
